/**
 * DriverDeliveryPage — DSL-driven multi-step delivery / exception workflow.
 *
 * Wizard steps, reason codes, and exception types are all defined in DSL config.
 * This page is a generic wizard renderer that reads from DSL definitions.
 *
 * URL: /driver/deliver/:tripId
 * Query: ?mode=exception  → uses EXCEPTION_WIZARD_DSL instead of DELIVERY_WIZARD_DSL
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, BASE } from '@/lib/api';
import { enqueueFileUpload } from '@/lib/offline-queue';
import { useAuth } from '@/lib/auth';
import { usePageParams } from '@/hooks/usePageParams';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronLeft, CheckCircle2, XCircle, AlertTriangle,
  ArrowRight, Send, Loader2, ChevronRight,
} from 'lucide-react';
import PhotoCapture from '@/components/driver/PhotoCapture';
import SignaturePad from '@/components/driver/SignaturePad';
import { getReasonCodes } from '@/components/driver/delivery-reason-codes';
import {
  DELIVERY_WIZARD_DSL,
  EXCEPTION_WIZARD_DSL,
} from '@/dsl/driver/driver-delivery.dsl';
import { EXCEPTION_TYPES } from '@/dsl/driver/driver-config';
import type { WizardStepDSL, FormFieldDSL } from '@/lib/use-view-template';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TripInfo {
  tripId: string;
  tripNo: string;
  tripType: string;
  tripStatus: string;
  jobNo: string;
  customerName: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DriverDeliveryPage() {
  const { tripId } = usePageParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const isExceptionMode = searchParams.get('mode') === 'exception';

  // Trip info
  const [trip, setTrip] = useState<TripInfo | null>(null);
  const [loadingTrip, setLoadingTrip] = useState(true);

  // Wizard state — keyed by field name
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [signature, setSignature] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  // Load trip info
  useEffect(() => {
    if (!tripId) return;
    setLoadingTrip(true);
    api.get<TripInfo[]>('/tms/mobile/my-trips')
      .then(data => {
        const found = data.find(t => t.tripId === tripId);
        setTrip(found ?? null);
      })
      .catch(() => setTrip(null))
      .finally(() => setLoadingTrip(false));
  }, [tripId]);

  // ── Resolve wizard DSL with dynamic options ──
  const wizardDsl = useMemo(() => {
    const baseDsl = isExceptionMode ? EXCEPTION_WIZARD_DSL : DELIVERY_WIZARD_DSL;
    if (!baseDsl.wizard) return null;

    // Deep clone steps to inject dynamic options
    const steps: WizardStepDSL[] = baseDsl.wizard.steps.map(step => ({
      ...step,
      fields: step.fields.map(field => {
        // Inject reason code options dynamically
        if (field.field === 'reasonCode' && trip) {
          const codes = getReasonCodes(trip.tripType);
          const outcomeVal = values.outcome as string | undefined;
          const codeList = outcomeVal === 'success' ? codes.success : codes.failure;
          return {
            ...field,
            label: outcomeVal === 'success' ? 'Delivery type' : 'Reason for failure',
            options: codeList.map(c => ({
              label: c.label,
              value: c.code,
              description: c.description,
            })),
          };
        }
        // Inject exception type options
        if (field.field === 'exceptionType') {
          return {
            ...field,
            options: EXCEPTION_TYPES.map(ex => ({
              label: ex.label,
              value: ex.type,
              description: ex.description,
            })),
          };
        }
        return field;
      }),
    }));

    return { ...baseDsl.wizard, steps };
  }, [isExceptionMode, trip, values.outcome]);

  const currentStep = wizardDsl?.steps[stepIndex];
  const isLastStep = wizardDsl ? stepIndex === wizardDsl.steps.length - 1 : false;

  // ── Field change handler ──
  const handleFieldChange = useCallback((field: string, value: unknown) => {
    setValues(prev => ({ ...prev, [field]: value }));

    // Auto-advance for card-select fields
    if (currentStep?.autoAdvance && wizardDsl) {
      setTimeout(() => {
        setStepIndex(prev => Math.min(prev + 1, (wizardDsl?.steps.length ?? 1) - 1));
      }, 200);
    }
  }, [currentStep, wizardDsl]);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    if (!trip || !tripId) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      let lat: number | undefined;
      let lng: number | undefined;
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* GPS unavailable */ }

      if (isExceptionMode && values.exceptionType) {
        await api.post(`/tms/mobile/trips/${tripId}/report-exception`, {
          exceptionType: values.exceptionType,
          description: (values.notes as string) || `Exception: ${values.exceptionType}`,
          severity: values.exceptionType === 'VEHICLE_BREAKDOWN' ? 'HIGH' : 'MEDIUM',
          lat,
          lng,
        });
      } else {
        const nextStatus = values.outcome === 'success' ? 'COMPLETED' : 'AT_DELIVERY';
        await api.post(`/tms/mobile/trips/${tripId}/update-status`, {
          status: nextStatus,
          lat,
          lng,
          notes: [
            values.reasonCode ? `[${values.reasonCode}]` : '',
            values.notes as string,
          ].filter(Boolean).join(' — '),
        });
      }

      // Upload photos
      const uploadErrors: string[] = [];
      for (const photo of photos) {
        try {
          const formData = new FormData();
          formData.append('file', photo);
          formData.append('referenceType', 'TRIP_POD');
          formData.append('referenceId', tripId);
          const token = localStorage.getItem('boox_token');
          const res = await fetch(`${BASE}/tms/mobile/pod-upload`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          });
          if (!res.ok) uploadErrors.push(`Photo upload failed (${res.status})`);
        } catch {
          // Offline — queue the file for later upload
          try {
            await enqueueFileUpload('/tms/mobile/pod-upload', photo, {
              referenceType: 'TRIP_POD',
              referenceId: tripId,
            });
          } catch {
            uploadErrors.push('Photo queue failed');
          }
        }
      }

      // Upload signature
      if (signature) {
        try {
          const blob = await (await fetch(signature)).blob();
          const sigFile = new File([blob], 'signature.png', { type: 'image/png' });
          const formData = new FormData();
          formData.append('file', sigFile);
          formData.append('referenceType', 'TRIP_SIGNATURE');
          formData.append('referenceId', tripId);
          const token = localStorage.getItem('boox_token');
          const res = await fetch(`${BASE}/tms/mobile/pod-upload`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          });
          if (!res.ok) uploadErrors.push(`Signature upload failed (${res.status})`);
        } catch {
          // Offline — queue signature for later upload
          try {
            const blob = await (await fetch(signature)).blob();
            const sigFile = new File([blob], 'signature.png', { type: 'image/png' });
            await enqueueFileUpload('/tms/mobile/pod-upload', sigFile, {
              referenceType: 'TRIP_SIGNATURE',
              referenceId: tripId,
            });
          } catch {
            uploadErrors.push('Signature queue failed');
          }
        }
      }

      if (uploadErrors.length > 0) {
        setSubmitError(`Status updated but POD uploads failed: ${uploadErrors.join(', ')}. Please retry.`);
      }

      setIsDone(true);
    } catch (err: unknown) {
      // Offline-queued mutations count as optimistic success
      if (err instanceof Error && 'code' in err && (err as { code: string }).code === 'OFFLINE_QUEUED') {
        setSubmitError('⏳ 已离线保存，恢复网络后将自动提交');
        setIsDone(true);
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [trip, tripId, values, photos, signature, isExceptionMode]);

  // ── Navigation ──
  const goBack = useCallback(() => {
    if (stepIndex === 0 || isDone) {
      navigate(`/driver/trip/${tripId}`);
    } else {
      setStepIndex(prev => prev - 1);
    }
  }, [stepIndex, isDone, navigate, tripId]);

  const goNext = useCallback(() => {
    if (wizardDsl && stepIndex < wizardDsl.steps.length - 1) {
      setStepIndex(prev => prev + 1);
    }
  }, [wizardDsl, stepIndex]);

  // ── Loading ──
  if (loadingTrip) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!trip || !wizardDsl) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16">
        <AlertTriangle className="h-8 w-8 text-destructive opacity-50 mb-2" />
        <p className="text-sm text-muted-foreground">Trip not found</p>
        <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/driver/trips')}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  // ── Done state ──
  if (isDone) {
    return (
      <div className="flex flex-col h-full">
        <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="ghost" onClick={() => navigate('/driver/trips')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-sm font-bold">
              {isExceptionMode ? 'Issue Reported' : 'Delivery Confirmed'}
            </h1>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center py-16 space-y-4">
          <div className={`rounded-full p-4 ${
            isExceptionMode ? 'bg-amber-100' : values.outcome === 'success' ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {isExceptionMode ? (
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            ) : values.outcome === 'success' ? (
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            ) : (
              <XCircle className="h-8 w-8 text-red-500" />
            )}
          </div>
          <div className="text-center">
            <p className="text-base font-semibold">
              {isExceptionMode ? 'Issue Reported' : values.outcome === 'success' ? 'Delivery Confirmed!' : 'Failure Recorded'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isExceptionMode
                ? 'Operations team has been notified'
                : values.outcome === 'success'
                  ? 'Proof of delivery has been saved'
                  : 'Operations team will follow up'}
            </p>
          </div>
          <Button onClick={() => navigate('/driver/trips')} className="mt-4">
            Back to Trips
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={goBack}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold">
              {isExceptionMode ? 'Report Issue' : 'Delivery Process'}
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {trip.tripNo || trip.tripId.slice(0, 8)} · {trip.jobNo}
            </p>
          </div>
          {/* Step indicator (DSL-driven) */}
          {wizardDsl.showStepIndicator && (
            <DslStepIndicator steps={wizardDsl.steps} currentIndex={stepIndex} />
          )}
        </div>
      </div>

      {/* ── Step content (rendered from DSL) ── */}
      <div className="flex-1 overflow-auto px-4 py-4">
        {submitting ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Submitting...</p>
          </div>
        ) : currentStep && (
          <div className="space-y-3">
            {/* Step description from DSL */}
            {currentStep.description && (
              <p className="text-sm font-medium">{currentStep.description}</p>
            )}

            {/* Render each field in the step */}
            {currentStep.fields.map(field => (
              <DslFieldRenderer
                key={field.field}
                field={field}
                value={values[field.field]}
                onChange={handleFieldChange}
                photos={photos}
                onPhotosChange={setPhotos}
                signature={signature}
                onSignatureChange={setSignature}
                onNext={goNext}
              />
            ))}

            {/* Step navigation buttons */}
            {!currentStep.autoAdvance && (
              <StepNavButtons
                step={currentStep}
                isLast={isLastStep}
                submitLabel={wizardDsl.submitLabel}
                onNext={goNext}
                onSubmit={handleSubmit}
                submitting={submitting}
                submitError={submitError}
                values={values}
                photos={photos}
                signature={signature}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DSL Step Indicator ──────────────────────────────────────────────────────

function DslStepIndicator({ steps, currentIndex }: { steps: WizardStepDSL[]; currentIndex: number }) {
  return (
    <div className="flex items-center gap-1">
      {steps.map((_, idx) => (
        <div
          key={idx}
          className={`h-1.5 rounded-full transition-all ${
            idx <= currentIndex ? 'w-4 bg-primary' : 'w-1.5 bg-muted'
          }`}
        />
      ))}
    </div>
  );
}

// ─── DSL Field Renderer (dispatches to specific widgets) ─────────────────────

function DslFieldRenderer({
  field,
  value,
  onChange,
  photos,
  onPhotosChange,
  signature,
  onSignatureChange,
  onNext,
}: {
  field: FormFieldDSL;
  value: unknown;
  onChange: (field: string, value: unknown) => void;
  photos: File[];
  onPhotosChange: (photos: File[]) => void;
  signature: string | null;
  onSignatureChange: (sig: string | null) => void;
  onNext: () => void;
}) {
  // Card-select: render options as clickable cards
  if (field.type === 'card-select') {
    const isGrid = field.renderAs === 'card-grid';
    return (
      <div className={isGrid ? 'grid grid-cols-2 gap-3' : 'space-y-2'}>
        {(field.options ?? []).map(option => {
          const isSelected = value === option.value;
          const colorClass = option.color === 'green'
            ? 'ring-2 ring-green-500 bg-green-50'
            : option.color === 'red'
              ? 'ring-2 ring-red-500 bg-red-50'
              : 'ring-2 ring-amber-500 bg-amber-50';

          return (
            <Card
              key={option.value}
              className={`cursor-pointer transition-all ${isSelected ? colorClass : 'hover:shadow-md'}`}
              onClick={() => onChange(field.field, option.value)}
            >
              <CardContent className={`${isGrid ? 'p-4 text-center space-y-2' : 'p-3 flex items-center justify-between'}`}>
                {isGrid ? (
                  <>
                    {option.icon === 'check-circle' && <CheckCircle2 className="h-8 w-8 mx-auto text-green-600" />}
                    {option.icon === 'x-circle' && <XCircle className="h-8 w-8 mx-auto text-red-500" />}
                    <p className="text-sm font-medium">{option.label}</p>
                    {option.description && (
                      <p className="text-[10px] text-muted-foreground">{option.description}</p>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-sm font-medium">{option.label}</p>
                      {option.description && (
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // Photo capture
  if (field.type === 'photo') {
    return (
      <PhotoCapture
        photos={photos}
        onAdd={(file) => onPhotosChange([...photos, file])}
        onRemove={(idx) => onPhotosChange(photos.filter((_, i) => i !== idx))}
        maxPhotos={field.maxItems ?? 5}
      />
    );
  }

  // Signature
  if (field.type === 'signature') {
    if (signature) {
      return (
        <div className="space-y-2">
          <div className="rounded-md border p-2 bg-white">
            <img src={signature} alt="Signature" className="max-h-32 mx-auto" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => onSignatureChange(null)}>
              Re-sign
            </Button>
            <Button className="flex-1" onClick={onNext}>
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      );
    }
    return (
      <SignaturePad
        onSign={(dataUrl) => {
          onSignatureChange(dataUrl);
          onNext();
        }}
        onSkip={onNext}
      />
    );
  }

  // Textarea
  if (field.type === 'textarea') {
    return (
      <Textarea
        placeholder={field.placeholder ?? 'Enter notes...'}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(field.field, e.target.value)}
        rows={3}
      />
    );
  }

  return null;
}

// ─── Step Navigation Buttons ─────────────────────────────────────────────────

function StepNavButtons({
  step,
  isLast,
  submitLabel,
  onNext,
  onSubmit,
  submitting,
  submitError,
  values,
  photos,
  signature,
}: {
  step: WizardStepDSL;
  isLast: boolean;
  submitLabel?: string;
  onNext: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitError: string | null;
  values: Record<string, unknown>;
  photos: File[];
  signature: string | null;
}) {
  // For photo step: show skip/next
  if (step.id === 'photos') {
    return (
      <div className="flex gap-2 pt-2">
        <Button variant="outline" className="flex-1" onClick={onNext}>
          {step.skipLabel ?? 'Skip'}
        </Button>
        {photos.length > 0 && (
          <Button className="flex-1" onClick={onNext}>
            {step.nextLabel ?? 'Next'} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    );
  }

  // For notes (last) step: show summary + submit
  if (isLast) {
    return (
      <div className="space-y-4">
        {/* Summary card */}
        <Card>
          <CardContent className="p-3 space-y-1 text-xs">
            {!!values.outcome && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Outcome</span>
                <Badge className={values.outcome === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}>
                  {values.outcome === 'success' ? 'Delivered' : 'Failed'}
                </Badge>
              </div>
            )}
            {!!values.exceptionType && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Issue Type</span>
                <span className="font-medium">{String(values.exceptionType).replace(/_/g, ' ')}</span>
              </div>
            )}
            {!!values.reasonCode && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reason</span>
                <span className="font-medium">{String(values.reasonCode)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Photos</span>
              <span>{photos.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Signature</span>
              <span>{signature ? '\u2713' : '\u2014'}</span>
            </div>
          </CardContent>
        </Card>

        {submitError && (
          <div className="text-xs text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            {submitError}
          </div>
        )}

        <Button className="w-full" onClick={onSubmit} disabled={submitting}>
          {submitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {submitLabel ?? 'Submit'}
        </Button>
      </div>
    );
  }

  // Generic next
  if (!step.autoAdvance) {
    return (
      <Button className="w-full" onClick={onNext}>
        {step.nextLabel ?? 'Next'} <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    );
  }

  return null;
}
